import React, { Component } from 'react'
import { withStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Chip from '@material-ui/core/Chip'
import Divider from '@material-ui/core/Divider'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import Icon from '@material-ui/core/Icon'
import Select from 'react-select'
import { CircularProgress } from '@material-ui/core'
import gql from 'graphql-tag'
import { Query, withApollo } from 'react-apollo'
import getLoginId from '../../../utils/getLoginId'
import ConfirmationalDialog from '../../common/ConfirmationDialog'
import withSharedSnackbar from '../../HOCs/withSharedSnackbar'

const styles = () => ({
  root: {
    padding: 24,
    marginTop: 24
  },
  paper: {
    padding: 20,
    marginBottom: 16
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: 12,
    color: '#2c3e50'
  },
  vehicleListSection: {
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 16
  },
  searchBar: {
    padding: '10px 16px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fff'
  },
  vehicleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 16px 6px 8px',
    borderBottom: '1px solid #f5f5f5',
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: '#fafafa' }
  },
  summaryCard: {
    backgroundColor: '#fff8f8',
    border: '1px solid #ffcccc',
    borderLeft: '4px solid #c62828',
    borderRadius: 6,
    padding: '12px 16px',
    marginBottom: 16
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': { borderBottom: 'none' }
  },
  warningBox: {
    backgroundColor: '#fff3e0',
    border: '1px solid #ff9800',
    borderRadius: 6,
    padding: '10px 16px',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  deleteBtn: {
    backgroundColor: '#c62828',
    color: '#fff',
    '&:hover': { backgroundColor: '#7f0000' },
    '&:disabled': { backgroundColor: '#bdbdbd', color: '#fff' }
  },
  emptyState: {
    padding: 32,
    textAlign: 'center',
    color: '#999'
  }
})

const GET_CLIENTS = gql`
  query allClientDetails($partnerLoginId: Int) {
    clients: allClientDetails(partnerLoginId: $partnerLoginId) {
      id
      clientName
      loginId
    }
  }
`

const GET_VEHICLES = gql`
  query getAllVehicleDetails($clientLoginId: Int) {
    vehicles: getAllVehicleDetails(clientLoginId: $clientLoginId) {
      entityId
      vehicleNumber
      clientLoginId
      deviceDetail {
        uniqueDeviceId
      }
      simDetail {
        phoneNumber
      }
      solutionType
    }
  }
`

// New dedicated bulk unregistration mutation
const BULK_UNREGISTER_VEHICLES = gql`
  mutation bulkUnregisterVehiclesonboarding(
    $entityIds: [Int!]!
    $uniqueids: [String!]!
    $reason: String!
    $created_by: String
    $updated_by: String
  ) {
    bulkUnregisterVehiclesonboarding(
      entityIds: $entityIds
      uniqueids: $uniqueids
      reason: $reason
      created_by: $created_by
      updated_by: $updated_by
    ) {
      success
      message
    }
  }
`

class VehicleUnregistration extends Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedCustomer: null,
      selectedVehicles: [],
      searchQuery: '',
      reason: '',
      isSubmitting: false,
      confirmDialogOpen: false,
      confirmDialogMessage: '',
      confirmDialogTitle: ''
    }
  }

  handleCustomerChange = selectedCustomer => {
    this.setState({ selectedCustomer, selectedVehicles: [], searchQuery: '', reason: '' })
  }

  handleSearchChange = e => {
    this.setState({ searchQuery: e.target.value })
  }

  clearSearch = () => {
    this.setState({ searchQuery: '' })
  }

  handleToggleVehicle = vehicle => {
    const { selectedVehicles } = this.state
    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []
    const exists = safeSelected.find(v => v.value === vehicle.value)
    if (exists) {
      this.setState({ selectedVehicles: safeSelected.filter(v => v.value !== vehicle.value) })
    } else {
      this.setState({ selectedVehicles: [...safeSelected, vehicle] })
    }
  }

  handleDeleteConfirm = () => {
    const { selectedVehicles, reason } = this.state
    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []

    if (safeSelected.length === 0) {
      this.props.openSnackbar('Please select at least one vehicle to unregister')
      return
    }
    if (!reason || reason.trim() === '') {
      this.props.openSnackbar('Please provide a reason for unregistration')
      return
    }

    const vehicleList = safeSelected.map(v => v.vehicleNumber).join(', ')
    this.setState({
      confirmDialogOpen: true,
      confirmDialogTitle: 'Confirm Unregistration',
      confirmDialogMessage: `⚠️ This action is IRREVERSIBLE.\n\nYou are about to permanently unregister ${safeSelected.length} vehicle(s):\n\n${vehicleList}\n\nAre you sure you want to proceed?`
    })
  }

  executeDelete = async () => {
    const { selectedVehicles, reason } = this.state
    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []

    const entityIds = safeSelected
      .map(v => parseInt(v.entityId || v.value, 10))
      .filter(id => !isNaN(id))

    const uniqueids = safeSelected
      .map(v => v.deviceDetail && v.deviceDetail.uniqueDeviceId)
      .filter(id => id)

    this.setState({ isSubmitting: true, confirmDialogOpen: false })

    try {
      const { data } = await this.props.client.mutate({
        mutation: BULK_UNREGISTER_VEHICLES,
        variables: {
          entityIds,
          uniqueids,
          reason: reason.trim(),
          created_by: localStorage.getItem('username'),
          updated_by: localStorage.getItem('username')
        },
        refetchQueries: ['getAllVehicleDetails']
      })

      const response = data && data.bulkUnregisterVehiclesonboarding
      this.props.openSnackbar((response && response.message) || `${entityIds.length} vehicle(s) unregistered successfully`)
      this.setState({ selectedVehicles: [], reason: '', searchQuery: '' })
    } catch (e) {
      console.error('Unregister error:', e)
      this.props.openSnackbar('Failed to unregister vehicle(s)')
    } finally {
      this.setState({ isSubmitting: false })
    }
  }

  closeConfirmDialog = () => {
    this.setState({ confirmDialogOpen: false })
  }

  renderVehicleList = (allVehicles, classes) => {
    const { selectedVehicles, searchQuery } = this.state
    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []
    const query = searchQuery.trim().toLowerCase()

    const filtered = query
      ? allVehicles.filter(v => {
        const matchVehicle = v.vehicleNumber && v.vehicleNumber.toLowerCase().includes(query)
        const matchDevice =
          v.deviceDetail &&
          v.deviceDetail.uniqueDeviceId &&
          v.deviceDetail.uniqueDeviceId.toLowerCase().includes(query)
        return matchVehicle || matchDevice
      })
      : allVehicles

    if (allVehicles.length === 0) {
      return (
        <div className={classes.emptyState}>
          <Typography variant="body2">No registered vehicles found for this customer</Typography>
        </div>
      )
    }

    return (
      <div className={classes.vehicleListSection}>
        {/* Search Bar */}
        <div className={classes.searchBar}>
          <TextField
            placeholder="Search by vehicle number or device ID..."
            value={searchQuery}
            onChange={this.handleSearchChange}
            fullWidth
            variant="outlined"
            size="small"
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon style={{ color: '#aaa', fontSize: 20 }}>search</Icon>
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Icon
                    style={{ color: '#aaa', fontSize: 18, cursor: 'pointer' }}
                    onClick={this.clearSearch}
                  >
                    close
                  </Icon>
                </InputAdornment>
              ) : null
            }}
          />
          {query && (
            <Typography variant="caption" style={{ color: '#666', marginTop: 4, display: 'block' }}>
              Showing {filtered.length} of {allVehicles.length} vehicles
              {safeSelected.length > 0 && ` • ${safeSelected.length} total selected`}
            </Typography>
          )}
        </div>

        {/* Vehicle Rows */}
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2" style={{ color: '#aaa' }}>
                No vehicles match &quot;{searchQuery}&quot;
              </Typography>
            </div>
          ) : (
            filtered.map(vehicle => {
              const isChecked = safeSelected.some(v => v.value === vehicle.value)
              return (
                <div
                  key={vehicle.value}
                  className={classes.vehicleRow}
                  onClick={() => this.handleToggleVehicle(vehicle)}
                  style={{ backgroundColor: isChecked ? '#fff8f8' : 'transparent' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={isChecked}
                      color="secondary"
                      style={{ padding: '0 8px 0 0' }}
                      onClick={e => e.stopPropagation()}
                      onChange={() => this.handleToggleVehicle(vehicle)}
                    />
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {vehicle.vehicleNumber}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#888' }}>
                        {vehicle.deviceDetail && vehicle.deviceDetail.uniqueDeviceId
                          ? vehicle.deviceDetail.uniqueDeviceId
                          : 'No Device'}
                        {vehicle.simDetail && vehicle.simDetail.phoneNumber
                          ? ` • SIM: ${vehicle.simDetail.phoneNumber}`
                          : ''}
                        {vehicle.solutionType ? ` • ${vehicle.solutionType.toUpperCase()}` : ''}
                      </Typography>
                    </div>
                  </div>
                  <Chip
                    label="Registered"
                    size="small"
                    style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7', fontSize: 11 }}
                  />
                </div>
              )
            })
          )}
        </div>
      </div>
    )
  }

  render() {
    const { classes } = this.props
    const {
      selectedCustomer,
      selectedVehicles,
      reason,
      isSubmitting,
      confirmDialogOpen,
      confirmDialogMessage,
      confirmDialogTitle
    } = this.state

    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []
    const partnerLoginId = getLoginId()

    return (
      <div className={classes.root}>
        <Typography variant="h4" component="h1" gutterBottom>
          Vehicle Unregistration
        </Typography>

        {/* Step 1: Select Customer & Vehicles */}
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Step 1 — Select Customer
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormGroup>
                  <Typography variant="subtitle2" gutterBottom>Select Customer *</Typography>
                  <Query query={GET_CLIENTS} variables={{ partnerLoginId }}>
                    {({ loading, error, data }) => {
                      if (loading) return <CircularProgress size={20} />
                      if (error) return <div>Error loading customers</div>
                      const customerOptions = (data.clients || []).map(c => ({
                        value: c.loginId,
                        label: c.clientName,
                        ...c
                      }))
                      return (
                        <Select
                          value={selectedCustomer}
                          onChange={this.handleCustomerChange}
                          options={customerOptions}
                          placeholder="Select a customer"
                        />
                      )
                    }}
                  </Query>
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Step 2: Select Vehicles */}
        {selectedCustomer && (
          <Paper className={classes.paper}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Step 2 — Select Vehicles to Unregister
              {safeSelected.length > 0 && (
                <span style={{ fontWeight: 400, fontSize: 13, color: '#c62828', marginLeft: 8 }}>
                  ({safeSelected.length} selected)
                </span>
              )}
            </Typography>

            <Query
              query={GET_VEHICLES}
              variables={{ clientLoginId: selectedCustomer.value }}
              fetchPolicy="network-only"
            >
              {({ loading, error, data }) => {
                if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress /></div>
                if (error) return <div style={{ padding: 16, color: 'red' }}>Error loading vehicles</div>

                const vehicleOptions = (data.vehicles || []).map(v => ({
                  value: v.entityId,
                  entityId: v.entityId,
                  vehicleNumber: v.vehicleNumber,
                  solutionType: v.solutionType,
                  deviceDetail: v.deviceDetail,
                  simDetail: v.simDetail
                }))

                return this.renderVehicleList(vehicleOptions, classes)
              }}
            </Query>
          </Paper>
        )}

        {/* Step 3: Summary & Confirm Delete */}
        {safeSelected.length > 0 && (
          <Paper className={classes.paper}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Step 3 — Review & Confirm Unregistration
            </Typography>

            {/* Warning Box */}
            <div className={classes.warningBox}>
              <Icon style={{ color: '#e65100', fontSize: 22 }}>warning</Icon>
              <Typography variant="body2" style={{ color: '#e65100', fontWeight: 500 }}>
                The following {safeSelected.length} vehicle{safeSelected.length !== 1 ? 's' : ''} will be
                permanently unregistered. This action cannot be undone.
              </Typography>
            </div>

            {/* Summary Table */}
            <div className={classes.summaryCard}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10, color: '#c62828' }}>
                Vehicles to be Unregistered ({safeSelected.length})
              </Typography>
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {safeSelected.map(v => (
                  <div key={v.value} className={classes.summaryRow}>
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {v.vehicleNumber}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#888' }}>
                        {v.deviceDetail && v.deviceDetail.uniqueDeviceId
                          ? `Device: ${v.deviceDetail.uniqueDeviceId}`
                          : 'No Device'}
                        {v.simDetail && v.simDetail.phoneNumber
                          ? ` • SIM: ${v.simDetail.phoneNumber}`
                          : ''}
                        {v.solutionType ? ` • ${v.solutionType.toUpperCase()}` : ''}
                      </Typography>
                    </div>
                    <Button
                      size="small"
                      onClick={e => { e.stopPropagation(); this.handleToggleVehicle(v) }}
                      style={{ color: '#c62828', minWidth: 0, padding: '2px 6px' }}
                      title="Remove from selection"
                    >
                      <Icon style={{ fontSize: 16 }}>close</Icon>
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <Divider style={{ marginBottom: 16 }} />

            {/* Reason & Submit */}
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={9}>
                <TextField
                  label="Reason for Unregistration *"
                  placeholder="Enter reason for unregistering these vehicles..."
                  value={reason}
                  onChange={e => this.setState({ reason: e.target.value })}
                  multiline
                  rows={2}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  fullWidth
                  className={classes.deleteBtn}
                  disabled={isSubmitting || !reason.trim()}
                  onClick={this.handleDeleteConfirm}
                >
                  {isSubmitting
                    ? <CircularProgress size={20} style={{ color: '#fff' }} />
                    : `Unregister (${safeSelected.length})`}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Confirm Dialog */}
        <ConfirmationalDialog
          isDialogOpen={confirmDialogOpen}
          dialogTitle={confirmDialogTitle}
          dialogMessage={confirmDialogMessage}
          positiveResponseButtonText="Yes, Unregister"
          negativeResponseButtonText="Cancel"
          positiveResponseHandler={this.executeDelete}
          negativeResponseHandler={this.closeConfirmDialog}
          dialogCloseHandler={this.closeConfirmDialog}
        />
      </div>
    )
  }
}

export default withStyles(styles)(
  withApollo(withSharedSnackbar(VehicleUnregistration))
)



// WEBPACK FOOTER //
// ./src/components/Pages/VehicleUnregistration/index.jsx